import React, { useEffect, useContext } from 'react';
import { SaveCardModal } from '@thd-olt-component-react/consumer-card-messaging';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import {
  CustomerAccountNavigation,
  useAccountCrumbs,
} from '@thd-olt-component-react/customer-account-navigation';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useNavigate, useLocation } from '@thd-olt-component-react/router';
import { useInterceptors } from '../hooks/use-interceptors';
import { Alert } from '../components/shared/alert/alert';
import { PageTitle } from '../components/shared/page-title';
import { PaymentsContent } from '../components/payments/payments-content/payments-content';
import {
  Crumb,
  StaticCrumb,
  mapCrumbsToStaticCrumbs,
} from '../utils/breadcrumbs';
import { LoyaltyMembershipDetailsProvider } from '../contexts/loyalty-membership-details-context';
import { usePayments } from '../hooks/use-payments';
import { PAYMENT_FORM_MODE } from '../models/common/payments';
import { TCustomerRole, TCustomerType } from '../models/common/customer';
import { TAlertStatus } from '../models/common/alert-messages';
import {
  ERR_BULK_ADD_NOT_AVAILABLE,
  ERR_IC_NOT_AVAILABLE_FOR_PRO_ACCOUNTS,
} from '../constants/response-error-messages';

const crumbs: Crumb[] = [
  {
    label: 'Cards & Accounts',
    path: '/myaccount/payments',
  },
];

const PaymentsPageContent = ({
  customerType,
  customerRole,
}: {
  customerType: TCustomerType;
  customerRole: TCustomerRole;
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { redirectError } = useInterceptors();
  const { isConsumerApp } = useContext(ExperienceContext);
  const { creditCardForm, setAlertMessage } = usePayments();
  const hdWalletFormMode =
    creditCardForm.mode === PAYMENT_FORM_MODE.AUTH_HD_WALLET_CREDIT_CARD;
  const notShowPagetitle = isConsumerApp && hdWalletFormMode;
  const staticCrumbs: StaticCrumb[] = mapCrumbsToStaticCrumbs(
    useAccountCrumbs(crumbs),
  );

  useEffect(() => {
    if (!state) return;

    const { hasICAccessErrorState, hasBulkAddAccessError } = state;

    let errorMessage = null;
    if (hasICAccessErrorState) {
      errorMessage = ERR_IC_NOT_AVAILABLE_FOR_PRO_ACCOUNTS.message;
    } else if (hasBulkAddAccessError) {
      errorMessage = ERR_BULK_ADD_NOT_AVAILABLE.message;
    }

    if (errorMessage) {
      setAlertMessage({
        alertStatus: TAlertStatus.ERROR,
        text: errorMessage,
      });
    }

    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const openAddCard = () => {
    navigate('/myaccount/payments/addcreditcard');
  };

  return (
    <div className="grid isBound">
      <div className="col__12-12">
        <ErrorBoundary name="save-card-modal">
          <SaveCardModal openAddCard={openAddCard} />
        </ErrorBoundary>
        {redirectError ? (
          <div className="sui-pt-8">
            <Alert status="info">{redirectError.message}</Alert>
          </div>
        ) : (
          <>
            <div className="sui-grid sui-gap-3 sui-py-3">
              {!isConsumerApp && (
                <ErrorBoundary name="breadcrumbs">
                  <Breadcrumbs staticCrumbs={staticCrumbs} />
                </ErrorBoundary>
              )}
              {!notShowPagetitle && (
                <PageTitle
                  title="CARDS & ACCOUNTS"
                  dataTestId="payments-title"
                />
              )}
            </div>

            <div className="sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3">
              <div className="sui-col-span-12 lg:sui-col-span-3">
                <ErrorBoundary name="customer-account-navigation">
                  <CustomerAccountNavigation />
                </ErrorBoundary>
              </div>

              <div className="sui-col-span-12 lg:sui-col-span-9">
                <ErrorBoundary name="payments-content">
                  <LoyaltyMembershipDetailsProvider>
                    <PaymentsContent
                      customerType={customerType}
                      customerRole={customerRole}
                    />
                  </LoyaltyMembershipDetailsProvider>
                </ErrorBoundary>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

PaymentsPageContent.dataModel = extend(
  CustomerAccountNavigation,
  LoyaltyMembershipDetailsProvider,
  Breadcrumbs,
);

PaymentsPageContent.displayName = 'PaymentsPageContent';

export const Payments = ({
  customerType,
  customerRole,
}: {
  customerType: TCustomerType;
  customerRole: TCustomerRole;
}) => {
  return (
    <QueryProvider
      defaultVariables={{
        channelId: 1,
      }}
      cacheKey="payments-page"
    >
      <PaymentsPageContent
        customerType={customerType}
        customerRole={customerRole}
      />
    </QueryProvider>
  );
};

Payments.displayName = 'Payments';
